import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Box,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { saveChapter, getChapterContent, fetchStoryDetails } from "../utils/api";
import { t } from "@lingui/macro";
import { useEditor, EditorContent } from "@tiptap/react";
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { Extension } from '@tiptap/core'
import { Plugin } from 'prosemirror-state'
import { DOMParser as ProseMirrorDOMParser } from "prosemirror-model";

function useAutosave(
  { storyID, chapterIndex, title, content },
  enabled = true,
  { delay = 3000, threshold = 20 } = {},
  onSaved = () => { }
) {
  const lastHtml = useRef("")
  const lastPlain = useRef("")
  const timer = useRef(null)
  const setBusy = useRef(null)
  const didInit = useRef(false)

  const getPlain = useCallback(html => {
    const div = document.createElement("div")
    div.innerHTML = html
    return (div.textContent || div.innerText || "").replace(/\r\n/g, "\n")
  }, [])

  useEffect(() => {
    if (!enabled) return

    const html = content
    const plain = getPlain(html)

    // first run: seed and bail
    if (!didInit.current) {
      didInit.current = true
      lastHtml.current = html
      lastPlain.current = plain
      return
    }

    // require a title and some content
    if (!title.trim() || plain === "") return

    // threshold or format change?
    const delta = Math.abs(plain.length - lastPlain.current.length)
    const isTextChange = delta >= threshold
    const isFormatOnly = plain === lastPlain.current && html !== lastHtml.current
    if (!isTextChange && !isFormatOnly) return

    // debounce save
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      try {
        setBusy.current(true)
        const contentBuffer = btoa(unescape(encodeURIComponent(html)))
        await saveChapter({
          storyID,
          chapterIndex,
          title,
          contentBuffer,
          autosave: true,
        })
        lastHtml.current = html
        lastPlain.current = plain
        onSaved()
      } finally {
        setBusy.current(false)
      }
    }, delay)

    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [
    storyID,
    chapterIndex,
    title,
    content,
    enabled,
    delay,
    threshold,
    getPlain,
    onSaved,
  ])

  return {
    setAutosaving: fn => (setBusy.current = fn),
  }
}

const ChapterEditorPage = () => {
  const { storyID, chapterIndex } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewRoute = location.state?.isNewChapter === true;
  const [skipLoad, setSkipLoad] = useState(isNewRoute);
  const [chapter, setChapter] = useState({ title: "", content: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(() => {
    const saved = localStorage.getItem("autosaveEnabled");
    return saved == null ? true : saved === "true";
  });
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(false); // ✅ Track if 500 error occurred
  const [lastSaved, setLastSaved] = useState({ title: "", content: "" });
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const isDirty = chapter.title !== lastSaved.title || chapter.content !== lastSaved.content;
  const titleRef = useRef(null);
  const [justSaved, setJustSaved] = useState(false);

  const triggerSavedPulse = useCallback(() => {
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 1000);
  }, []);

  const PasteAsPlainText = Extension.create({
    name: 'pasteAsPlainText',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            handlePaste(view, event) {
              // Prevent default rich-text/HTML paste
              event.preventDefault();
              const clipboardText = event.clipboardData.getData("text/plain");
              if (!clipboardText) return false;

              // Split on newline, trim, and filter out empty lines
              const paragraphs = clipboardText
                .split(/\r?\n/)
                .map(line => line.trim())
                .filter(line => line.length > 0);

              // Wrap each paragraph in a <p> tag and join them into one HTML string
              const html = paragraphs.map(text => `<p>${text}</p>`).join('');

              // Retrieve editor state and dispatch function
              const { state, dispatch } = view;
              // Create a ProseMirror DOM parser from the current schema
              const parser = ProseMirrorDOMParser.fromSchema(state.schema);
              // Create a temporary container and set its innerHTML
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = html;
              // Parse the HTML from the temporary container into a Slice
              const slice = parser.parseSlice(tempDiv, { preserveWhitespace: true });

              // Replace the current selection with the parsed Slice
              dispatch(state.tr.replaceSelection(slice).scrollIntoView());
              //setChapter(prev => ({ ...prev, content: html }));
              return true;
            },
          },
        }),
      ];
    },
  });

  const ShiftEnterAsNewParagraph = Extension.create({
    name: 'shiftEnterAsNewParagraph',
    addKeyboardShortcuts() {
      return {
        'Shift-Enter': () => {
          // Split the current block (creating a new paragraph).
          return this.editor.commands.splitBlock();
        },
      };
    },
  });

  const CtrlEnterAsNewParagraph = Extension.create({
    name: 'ctrlEnterAsNewParagraph',
    addKeyboardShortcuts() {
      return {
        'Mod-Enter': () => {
          // Call the same command as splitBlock() to create a new paragraph.
          return this.editor.commands.splitBlock();
        },
      };
    },
  });


  // Initialize the Tiptap editor
  const editor = useEditor({
    extensions: [
      // StarterKit with everything except Bold, Italic, Paragraph, Heading, History
      StarterKit.configure({
        // Keep these features:
        paragraph: true,
        bold: true,
        italic: true,
        history: true,

        // Disable everything else:
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        blockquote: false,
        strike: false,
        dropcursor: false,
        gapcursor: false,
        // etc.
      }),

      // Add underline and text alignment
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      PasteAsPlainText,
      ShiftEnterAsNewParagraph,
      CtrlEnterAsNewParagraph,
    ],
    content: chapter.content,
    onUpdate: ({ editor }) => {
      setError(null)
      const html = editor.getHTML()
      setChapter(prev => ({ ...prev, content: html }))
    },

  });

  const getPlainText = () => {
    let text;
    if (editor) {
      text = editor.getText();
    } else {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = chapter.content;
      text = tempDiv.textContent || tempDiv.innerText || "";
    }
    // Normalize CRLF to LF and then remove all newline characters
    return text.replace(/\r\n/g, "\n").replace(/\n/g, "");
  };

  const wordCount = getPlainText().trim().split(/\s+/).filter(Boolean).length;

  useEffect(() => {
    const loadChapterContent = async () => {

      if (skipLoad) {
        setLoading(false);
        return;
      }

      let contentUrl = location.state?.contentUrl;
      let initialUpdatedAt = location.state?.updatedAt;
      let fetchedTitle;

      // 3️⃣ …but if there’s none (deep link / hard refresh), pull it from the story doc:
      if (!contentUrl) {
        try {
          const story = await fetchStoryDetails(storyID);
          const chap = story.chapters?.[chapterIndex];
          if (chap?.contentUrl) {
            contentUrl = chap.contentUrl;
            // Inside your loadChapterContent effect, replace the chap?.contentUrl block with this:

            if (chap?.contentUrl) {
              contentUrl = chap.contentUrl;

              // normalize Firestore‐style {_seconds, _nanoseconds}, real Timestamp, string or number → JS Date
              if (chap.updatedAt?._seconds != null) {
                initialUpdatedAt = new Date(
                  chap.updatedAt._seconds * 1000 +
                  chap.updatedAt._nanoseconds / 1e6
                );
              } else if (chap.updatedAt instanceof Date) {
                initialUpdatedAt = chap.updatedAt;
              } else if (typeof chap.updatedAt.toDate === 'function') {
                initialUpdatedAt = chap.updatedAt.toDate();
              } else {
                initialUpdatedAt = new Date(chap.updatedAt);
              }

              fetchedTitle = chap.title;
              setChapter(c => ({ ...c, title: chap.title || c.title }));
            }

            fetchedTitle = chap.title;
            // also prefill the title if you want:
            setChapter(c => ({ ...c, title: chap.title || c.title }));
          }
        } catch (err) {
          console.error("Failed to fetch story for deep‑link:", err);
        }
      }

      // ✅ If no `contentUrl`, assume new chapter
      if (!contentUrl) {
        console.warn("⚠️ No content URL provided. Assuming new chapter.");
        setLoading(false);
        return;
      }

      try {
        const content = await getChapterContent(contentUrl);

        const initialTitle =
          location.state?.title      // whatever your router passed
          ?? fetchedTitle            // or the one we just fetched
          ?? "";                     // or empty if brand new

        if (content === null) {
          console.warn("⚠️ Chapter does not exist yet. Starting fresh.");
        } else {
          const htmlContent = convertPlainTextToHTML(content);
          setChapter({ title: initialTitle, content: htmlContent });
          if (editor) {
            editor.commands.setContent(htmlContent);
          }
          setLastSaved({
            title: initialTitle,
            content: htmlContent
          })
          // normalize Firestore Timestamp / string / number / Date → JS Date
          if (initialUpdatedAt) {
            const parsed =
              typeof initialUpdatedAt.toDate === 'function'
                ? initialUpdatedAt.toDate()           // Firestore Timestamp
                : new Date(initialUpdatedAt);         // string | number | Date
            setLastSavedAt(parsed);
          } else {
            setLastSavedAt(null);
          }
        }

      } catch (err) {
        console.error("Error loading chapter content:", err);

        if (err.message.includes("500")) {
          setServerError(true);
          setError(t`Server error: Failed to load chapter. Please refresh in a while.`);
        } else {
          setError(t`Failed to load chapter content.`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadChapterContent();
  }, [storyID, chapterIndex, skipLoad, location.state, editor]);


  useEffect(() => {
    if (editor && chapter.content && editor.getHTML() !== chapter.content) {
      editor.commands.setContent(chapter.content);
    }
  }, [editor, chapter.content]);


  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  const { setAutosaving: _setAutosaving } = useAutosave(
    {
      storyID,
      chapterIndex: parseInt(chapterIndex, 10),
      title: chapter.title,
      content: chapter.content,
    },
    // only enable autosave after load finishes
    autosaveEnabled && !loading,
    { delay: 5000, threshold: 30 },
    (newTimestamp) => {
      setLastSaved({ title: chapter.title, content: chapter.content });
      setLastSavedAt(newTimestamp ? new Date(newTimestamp) : new Date());
      triggerSavedPulse();
      if (skipLoad) setSkipLoad(false);
      window.history.replaceState({}, '', window.location.href);
    }
  );


  useEffect(() => {
    _setAutosaving(setAutosaving);
  }, [_setAutosaving]);

  const handleSaveChapter = async () => {
    setError(null);

    // ✅ Title validation (1-40 characters)
    const titleLength = chapter.title.trim().length;
    if (titleLength < 1 || titleLength > 40) {
      setError(t`Chapter title must be between 1 and 40 characters.`);
      titleRef.current?.focus();
      return;
    }

    // ✅ Content validation (1-60,000 characters)
    const contentHTML = editor ? editor.getHTML() : chapter.content;
    const contentLength = contentHTML.trim().length;
    if (contentLength < 1 || contentLength > 60000) {
      setError(t`Chapter content must be between 1 and 20,000 characters.`);
      editor?.chain().focus().run();
      return;
    }


    setSaving(true);
    try {
      const contentBuffer = btoa(unescape(encodeURIComponent(contentHTML)));
      const { updatedAt } = await saveChapter({
        storyID,
        chapterIndex: parseInt(chapterIndex, 10),
        title: chapter.title,
        contentBuffer,
      });
      setLastSaved({ title: chapter.title, content: chapter.content });
      setLastSavedAt(updatedAt ? new Date(updatedAt) : new Date());
      triggerSavedPulse();
      setSkipLoad(false);
      navigate(`/mystories/${storyID}`);
    } catch (err) {
      setError(t`Failed to save chapter.`);
    } finally {
      setSaving(false);
    }
  };

  const convertPlainTextToHTML = (text) => {
    // If text already contains HTML, assume it's already formatted.
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return text;
    }
    // Split text by single newline and wrap each line in a <p> tag.
    return text
      .split('\n')
      .map(line => `<p>${line.trim()}</p>`)
      .join('');
  };

  const helperColor =
    chapter.content.length > 60000
      ? "error.main"
      : chapter.content.length >= 5000
        ? "success.main"
        : "warning.main";


  if (loading)
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );

  return (
    <Container maxWidth="lg" sx={{ marginY: 4 }}>
      <Paper
        sx={{
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          position: "relative",
          pb: 2,
        }}
      >
        {/* Chapter Title */}
        <Typography
          variant="h6"
          gutterBottom
          sx={{ textAlign: "center", marginBottom: 2 }}
        >
          {t`Editing Chapter ${chapterIndex}`}
        </Typography>

        {/* Error Messages */}
        {error && (
          <Alert severity={serverError ? "error" : "warning"} sx={{ marginBottom: 2 }}>
            {error} {serverError && t` Try refreshing the page in a while.`}
          </Alert>
        )}

        {/* Chapter Title Input */}
        <TextField
          inputRef={titleRef}
          label={t`Chapter Title`}
          variant="standard"
          fullWidth
          name="title"
          value={chapter.title}
          onChange={(e) => {
            setError(null)
            setChapter({ ...chapter, title: e.target.value })
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: 40 }}
          helperText={`${chapter.title.length}/40`}
          sx={{ marginBottom: 4 }}
        />
        {/* Floating Formatting Toolbar */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paperAlternative',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            padding: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ToggleButtonGroup aria-label="text formatting" size="small">
            <ToggleButton
              value="bold"
              selected={editor?.isActive('bold')}
              onClick={() => editor?.chain().focus().toggleBold().run()}
              aria-label="bold"
              size="small"
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="italic"
              selected={editor?.isActive('italic')}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              aria-label="italic"
              size="small"
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="underline"
              selected={editor?.isActive('underline')}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              aria-label="underline"
              size="small"
            >
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={
              editor?.isActive({ textAlign: 'left' })
                ? 'left'
                : editor?.isActive({ textAlign: 'center' })
                  ? 'center'
                  : editor?.isActive({ textAlign: 'right' })
                    ? 'right'
                    : null
            }
            exclusive
            onChange={(event, newAlignment) => {
              if (newAlignment) {
                editor?.chain().focus().setTextAlign(newAlignment).run();
              }
            }}
            aria-label="text alignment"
            size="small"
          >
            <ToggleButton value="left" aria-label="left aligned" size="small">
              <FormatAlignLeftIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="center" aria-label="center aligned" size="small">
              <FormatAlignCenterIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="right" aria-label="right aligned" size="small">
              <FormatAlignRightIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Tiptap Editor for Chapter Content */}
        <Box
          onClick={() => editor?.chain().focus().run()} // Ensure editor gets focus on click
          sx={(theme) => ({
            position: "relative",
            overflowY: "auto",
            maxHeight: "80vh",
            minHeight: "20vh",
            // Example: styling the scrollbar to match your MUI theme
            "&::-webkit-scrollbar": {
              width: "8px", // scrollbar thickness
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.paper,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: theme.palette.text.secondary,
            },

            // For Firefox support (limited styling):
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.divider} transparent`, // thumb color, track color

            // Use a bottom border that changes thickness on hover/focus
            borderBottom: `1px solid transparent`,
            "&:hover": {
              borderBottom: `2px solid ${theme.palette.secondary.main}`,
            },
            "&:focus-within": {
              borderBottom: `3px solid ${theme.palette.secondary.main}`,
            },

            // Tiptap's ProseMirror editor
            "& .ProseMirror": {
              fontFamily: "Arial, sans-serif",
              fontSize: "1rem",
              lineHeight: 1.6,
              "&:focus, &:focus-visible": {
                outline: "none",
              },
              "& h1": {
                fontSize: "2rem",
                margin: "1rem 0",
              },
              "& p": {
                margin: "0.75rem 0",
              },
            },

            "& .ProseMirror": {
              fontFamily: "Arial, sans-serif",
              fontSize: "1rem",
              lineHeight: 1.6,
              "&:focus, &:focus-visible": {
                outline: "none",
              },
              "& h1": { fontSize: "2rem", margin: "1rem 0" },
              "& p": { margin: "0.75rem 0" },
            },

            // Tiptap's ProseMirror editor
            "& .ProseMirror": {
              fontFamily: "Arial, sans-serif",
              fontSize: "1rem",
              lineHeight: 1.6,

              // Remove default browser focus outline on contenteditable
              "&:focus, &:focus-visible": {
                outline: "none",
              },

              "& h1": {
                fontSize: "2rem",
                margin: "1rem 0",
              },
              "& p": {
                margin: "0.75rem 0",
              },
            },
          })}
        >
          <EditorContent editor={editor} />
        </Box>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paperAlternative',
            zIndex: 1100,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            p: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            mt: 2,
          }}
        >
          {/* Character & Word counts */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="span" sx={{ fontSize: '0.8rem' }} color={helperColor}>
              {t`${getPlainText().length} / 20000 (min 5000 to publish)`}
            </Typography>
            <Typography component="span" variant="caption">
              {t`Word count: ${wordCount}`}
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {saving
                  ? t`Saving…`
                  : autosaving
                    ? t`Autosaving…`
                    : lastSavedAt
                      ? (
                        <>
                          <SaveIcon
                            color={isDirty ? 'warning' : 'success'}
                            fontSize="small"
                            sx={{ verticalAlign: 'middle' }}
                          />
                          {lastSavedAt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'numeric' })}{' '}
                          {lastSavedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </>
                      )
                      : null
                }
              </Typography>
            </Box>
          </Box>

          {/* Save button with toggle underneath */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Button
              variant="contained"
              color={justSaved ? "success" : "primary"}
              onClick={handleSaveChapter}
              disabled={saving || autosaving}
              startIcon={
                saving || autosaving
                  ? <CircularProgress size={20} color="inherit" />
                  : justSaved
                    ? <CheckIcon fontSize="small" />
                    : null
              }
              sx={{
                padding: 0.8,
                fontSize: 12,
                textTransform: 'none',
                minWidth: 100,
                ...(justSaved && {
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' },
                }),
              }}
            >
              {saving
                ? t`Saving…`
                : autosaving
                  ? t`Autosaving…`
                  : justSaved
                    ? t`Saved`
                    : t`Save Chapter`}
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={autosaveEnabled}
                  onChange={(e) => {
                    setAutosaveEnabled(e.target.checked);
                    localStorage.setItem("autosaveEnabled", e.target.checked);
                  }}
                  size="small"
                  color="success"  // green when on, grey when off
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: 10 }}>
                  {t`Autosave`}
                </Typography>
              }
              labelPlacement="start"
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

      </Paper>
    </Container >
  );
};

export default ChapterEditorPage;
